import './index.css';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useId, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { isEventFromInteractiveComponent } from '@hh.ru/magritte-common-mouse';
import { Selection } from '@hh.ru/magritte-common-text-selection';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Action } from '@hh.ru/magritte-ui-action';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { CrossOutlinedSize16, CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { useFocus } from './useFocus.js';
import { useInputAriaProps } from './useInputAriaProps.js';
import { Text } from '@hh.ru/magritte-ui-typography';

var styles = {"input":"magritte-input___LVTID_7-1-17","label":"magritte-label___YlOQk_7-1-17","elevated":"magritte-elevated___kPcsM_7-1-17","focused":"magritte-focused___dxrHD_7-1-17","invalid":"magritte-invalid___L5MVa_7-1-17","disabled":"magritte-disabled___0NH6E_7-1-17","position-container":"magritte-position-container___kZnvZ_7-1-17","positionContainer":"magritte-position-container___kZnvZ_7-1-17","field":"magritte-field___9S8Tw_7-1-17","hint":"magritte-hint___EFGK0_7-1-17","additional-value":"magritte-additional-value___9Kyap_7-1-17","additionalValue":"magritte-additional-value___9Kyap_7-1-17","label_elevated":"magritte-label_elevated___PVcpC_7-1-17","labelElevated":"magritte-label_elevated___PVcpC_7-1-17","fixed-placeholder":"magritte-fixed-placeholder___PYJIH_7-1-17","fixedPlaceholder":"magritte-fixed-placeholder___PYJIH_7-1-17","icon":"magritte-icon___HEDsQ_7-1-17","focus-visible":"magritte-focus-visible___QvCO1_7-1-17","focusVisible":"magritte-focus-visible___QvCO1_7-1-17","right-container":"magritte-right-container___N0B0o_7-1-17","rightContainer":"magritte-right-container___N0B0o_7-1-17","with-icon":"magritte-with-icon___TPBe2_7-1-17","withIcon":"magritte-with-icon___TPBe2_7-1-17","large":"magritte-large___9frRh_7-1-17","small":"magritte-small___iQL4F_7-1-17","autofill-helper":"magritte-autofill-helper___WFjOW_7-1-17","autofillHelper":"magritte-autofill-helper___WFjOW_7-1-17","content-container":"magritte-content-container___s-0kX_7-1-17","contentContainer":"magritte-content-container___s-0kX_7-1-17","rigging-container":"magritte-rigging-container___Up9Lb_7-1-17","riggingContainer":"magritte-rigging-container___Up9Lb_7-1-17","input__mask":"magritte-input__mask___CHda2_7-1-17","inputMask":"magritte-input__mask___CHda2_7-1-17","value-ghost":"magritte-value-ghost___IXNZj_7-1-17","valueGhost":"magritte-value-ghost___IXNZj_7-1-17","hint-container":"magritte-hint-container___rGsD8_7-1-17","hintContainer":"magritte-hint-container___rGsD8_7-1-17","counter":"magritte-counter___Bld3Q_7-1-17","value-container":"magritte-value-container___6ReT7_7-1-17","valueContainer":"magritte-value-container___6ReT7_7-1-17"};

const matchIconSize = (size, icon) => {
    return icon &&
        (((size === 'medium' || size === 'large') && icon._size === 24) ||
            (size === 'small' && icon._size === 16))
        ? icon
        : null;
};
const BaseInputForwardRefRenderFunc = ({ onChange, onFocus, onBlur, value, placeholder, disabled = false, postfix, size = 'medium', elevatePlaceholder = false, invalid = false, description, errorMessage, formatter, buttonIcon: _buttonIcon, clearable = false, clearableOnFocus, onButtonClick, buttonDataQa, buttonRef, inputIcon, wrapperRef, fixedPlaceholder, maxLength, hint, showHintAlways = false, ...restProps }, ref) => {
    const wrapperCurrentRef = useRef(null);
    const inputRef = useRef(null);
    const [isActionVisible, setActionVisible] = useState(false);
    const inputRefCallback = useMultipleRefs(ref, inputRef);
    const wrapperRefCallback = useMultipleRefs(wrapperRef, wrapperCurrentRef);
    const descriptionId = useId();
    const labelId = useId();
    const hintRef = useRef(null);
    const hintMaxWidthRef = useRef(null);
    const change = useCallback((newValue) => {
        if (inputRef.current && formatter) {
            const caretPosition = Selection.getCaretPosition(inputRef.current);
            const formatResult = formatter(value, newValue, caretPosition || 0);
            requestAnimationFrame(() => {
                if (inputRef.current) {
                    Selection.setCaretPosition(inputRef.current, formatResult.caretPosition);
                }
            });
            onChange(formatResult.outputValue);
            return;
        }
        onChange(newValue);
    }, [formatter, onChange, value, inputRef]);
    const clearClickHandler = useCallback((event) => {
        onChange('');
        inputRef.current && inputRef.current.focus();
        onButtonClick?.(event);
    }, [onChange, inputRef, onButtonClick]);
    const forceDisabled = useDisabled();
    const isComponentDisabled = forceDisabled || !!disabled;
    const { changeHandler, focusHandler, blurHandler, isFocused } = useFocus(change, onFocus, onBlur);
    const placeholderCanBeElevated = size === 'large' && !!elevatePlaceholder && !!placeholder;
    const [isFocusVisible, setFocusVisible] = useState(false);
    const currentValue = formatter?.('', value, 0).formattedValue || value;
    const buttonQaLabel = buttonDataQa || `input-${clearable ? 'clearable-' : ''}button`;
    const ariaProps = useInputAriaProps({
        invalid,
        hasDescription: !!description,
        hasPlaceholder: !!placeholder,
        descriptionId,
        labelId,
    });
    const handleWrapperFocus = () => {
        setActionVisible(true);
    };
    const handleWrapperBlur = () => {
        requestAnimationFrame(() => {
            if (!wrapperCurrentRef.current?.contains(document.activeElement)) {
                setActionVisible(false);
            }
        });
    };
    const onClickWrapper = (e) => {
        if (!isEventFromInteractiveComponent(e)) {
            inputRef.current?.focus();
        }
    };
    const focusedOrFilled = isFocused || value !== '';
    const placeholderIsElevated = focusedOrFilled && placeholderCanBeElevated;
    const showPostfix = !!postfix && (placeholderIsElevated || value !== '');
    const showFixedPlaceholder = focusedOrFilled && !!fixedPlaceholder;
    const showLabel = placeholderCanBeElevated || (value === '' && !showFixedPlaceholder);
    const InputIconComponent = matchIconSize(size, inputIcon);
    const showClearButton = value.length > 0 && (clearable || (!!clearableOnFocus && isActionVisible));
    const showHint = !!hint && (showHintAlways || value.length > 0);
    const buttonIcon = matchIconSize(size, _buttonIcon);
    const hintWrapperRefCallback = useCallback((element) => {
        if (element && hintRef.current) {
            const text = hintRef.current.innerText;
            if (text.length > 30) {
                let maxWidth;
                if (text !== hintMaxWidthRef.current?.text) {
                    const newText = text.slice(0, 30);
                    hintRef.current.innerText = newText;
                    maxWidth = hintRef.current.offsetWidth;
                    hintRef.current.innerText = text;
                    hintMaxWidthRef.current = {
                        width: maxWidth,
                        text,
                    };
                }
                else {
                    maxWidth = hintMaxWidthRef.current.width;
                }
                element.style.maxWidth = `${maxWidth}px`;
            }
        }
    }, 
    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    [hint]);
    useEffect(() => {
        if (inputRef.current) {
            setFocusVisible(isFocused && inputRef.current.classList.contains('focus-visible'));
        }
    }, [isFocused]);
    return (jsxs("div", { ref: wrapperRefCallback, className: classnames(styles.input, {
            [styles.focused]: isFocused,
            [styles.focusVisible]: isFocusVisible,
            [styles.disabled]: isComponentDisabled,
            [styles.large]: size === 'large',
            [styles.small]: size === 'small',
            [styles.elevated]: placeholderCanBeElevated,
            [styles.invalid]: !!invalid,
            [styles.withIcon]: !!inputIcon,
        }), onFocus: clearableOnFocus ? handleWrapperFocus : undefined, onBlur: clearableOnFocus ? handleWrapperBlur : undefined, onClick: onClickWrapper, "data-interactive": true, children: [jsxs("div", { className: styles.positionContainer, children: [jsxs("div", { className: styles.hintContainer, children: [jsx("div", { className: styles.autofillHelper }), jsxs("div", { className: styles.contentContainer, children: [!!InputIconComponent && (jsx("div", { className: styles.icon, children: jsx(InputIconComponent, {}) })), jsxs("div", { className: styles.valueContainer, children: [jsx("input", { ...restProps, ...ariaProps, maxLength: maxLength, disabled: isComponentDisabled, suppressHydrationWarning: true, className: styles.field, value: currentValue, onInput: changeHandler, onFocus: focusHandler, onBlur: blurHandler, ref: inputRefCallback }), jsx("div", { className: styles.additionalValue, children: jsxs(Text, { Element: "span", typography: size === 'small' ? 'label-3-regular' : 'label-2-regular', children: [jsx("span", { className: styles.valueGhost, children: currentValue }), showFixedPlaceholder && (jsx("span", { className: styles.fixedPlaceholder, children: fixedPlaceholder.slice(value.length) })), showPostfix && (jsxs(Fragment, { children: ['\u00A0', postfix] }))] }) })] }), jsx("div", { className: styles.riggingContainer, children: showLabel && (jsx("label", { className: classnames(styles.label, {
                                                [styles.labelElevated]: placeholderIsElevated,
                                            }), id: ariaProps['aria-labelledby'], children: placeholder })) })] }), showHint && (jsx("div", { className: styles.hint, ref: hintWrapperRefCallback, "data-qa": "input-hint", children: jsx(Text, { Element: "span", typography: size === 'small' ? 'label-4-regular' : 'label-3-regular', children: jsx("span", { ref: hintRef, children: hint }) }) }))] }), (buttonIcon || showClearButton) && (jsx("div", { className: styles.rightContainer, children: (buttonIcon || showClearButton) && (jsx(Action, { "data-qa": buttonQaLabel, mode: "secondary", onMouseDown: (event) => event.preventDefault(), onClick: showClearButton ? clearClickHandler : onButtonClick, icon: showClearButton
                                ? (size === 'small' && CrossOutlinedSize16) || CrossOutlinedSize24
                                : buttonIcon, disabled: isComponentDisabled, ref: buttonRef })) }))] }), jsx(FormHelper, { description: description, errorMessage: errorMessage, descriptionId: ariaProps['aria-describedby'], disabled: isComponentDisabled, invalid: invalid })] }));
};
const BaseInput = forwardRef(BaseInputForwardRefRenderFunc);
BaseInput.displayName = 'BaseInput';

export { BaseInput };
//# sourceMappingURL=BaseInput.js.map
